import { EmptyPage } from "@components/common/empty-page/empty-page";
import { WarningTwoToneIcon } from "@components/common/icons";
import { PreservingNavigate } from "@components/common/preserve-navigation/preserving-navigate";
import { SphereButton } from "@components/common/sphere-button";
import { ContactSupportLocations } from "@custom-types/types";
import { LoadingScreen } from "@flat-ui";
import { NotFoundPage } from "@pages/not-found-page";
import {
  AccountSettingsTabs,
  MainRoutes,
  ProjectTabs,
} from "@router/route-params";
import {
  generateAccountSettingsRoute,
  generateProjectDetailsRoute,
  generateTeamsRoute,
  useAppParams,
} from "@router/router-helper";
import { selectedProjectIdSelector } from "@store/projects/projects-selector";
import { useAppSelector } from "@store/store-helper";
import { fetchingUserFlagsSelector } from "@store/user/user-selector";
import { contactSupport } from "@utils/email-utils";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useLocation } from "react-router-dom";

// Another page with an instant redirect can be found at .../pages/data-management/data-management.tsx
// It has its own implementation since it needs to call trackOpenProject before redirecting, which isn't
// possible with the PreservingNavigate element used here.

/** List of all routes of HDB that are deprecated and not planned yet to implement in SDB */
const deprecatedFeatureRoutes = ["/settings/templates/categories"];

/**
 * Manages redirection of the unknown routes.
 */
export function RedirectUnknownRoutes(): JSX.Element {
  const location = useLocation();
  const { trackEvent } = useTrackEvent();

  const { companyId } = useAppParams();
  const projectId = useAppSelector(selectedProjectIdSelector);
  const { isFetchingCurrentUser } = useAppSelector(fetchingUserFlagsSelector);

  const currentPath = location.pathname;
  const currentPathWithoutCompanyId = currentPath.replace(`/${companyId}`, "");
  const normalizedCurrentPath = currentPathWithoutCompanyId.endsWith("/")
    ? currentPathWithoutCompanyId.slice(0, -1)
    : currentPathWithoutCompanyId;

  /** Showing a loading screen before initiate the redirection */
  if (!companyId || isFetchingCurrentUser) {
    return <LoadingScreen />;
  }

  /** Redirect users from HBD route "/me" to the account settings page */
  if (normalizedCurrentPath === "/me") {
    return <PreservingNavigate to={generateAccountSettingsRoute()} />;
  }

  /** Redirect user from the HBD route "/settings/integrations" to the integrations page */
  if (companyId && normalizedCurrentPath === "/settings/integrations") {
    return (
      <PreservingNavigate
        to={generateAccountSettingsRoute(AccountSettingsTabs.integrations)}
      />
    );
  }

  /** Redirect correct teams route if user tries to access "/teams" as main route directly */
  if (companyId && normalizedCurrentPath === `/${MainRoutes.teams}`) {
    return <PreservingNavigate to={generateTeamsRoute(companyId)} />;
  }

  /** Redirect to project-member page if user tries to access project-team URL */
  if (
    companyId &&
    projectId &&
    normalizedCurrentPath.includes(`/${MainRoutes.projects}`) &&
    normalizedCurrentPath.endsWith("/team")
  ) {
    return (
      <PreservingNavigate
        to={generateProjectDetailsRoute(
          companyId,
          projectId,
          ProjectTabs.members
        )}
      />
    );
  }

  /** Redirect to deprecated feature page if the routes is pointing to deprecated features of HBD*/
  if (deprecatedFeatureRoutes.includes(normalizedCurrentPath)) {
    return (
      <EmptyPage
        title="The feature is no longer supported"
        subtitle="The feature you are looking for is no longer supported in Sphere XG"
        icon={WarningTwoToneIcon}
        button={
          <SphereButton
            onClick={() =>
              contactSupport({
                location: ContactSupportLocations.settingsDeprecatedPage,
                trackEvent,
              })
            }
          >
            Contact support
          </SphereButton>
        }
      />
    );
  }

  return <NotFoundPage />;
}
